/**
 * Module dependencies.
 */

import { Button, Loading, Type } from 'src/components/core';
import { ReactElement, useEffect } from 'react';
import { StepPageConfig } from 'src/types/advisor';
import { color, media, units } from '@untile/react-components';
import { useAdvisor } from 'src/context/advisor/context';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useLocomotiveScroll } from 'react-locomotive-scroll';
import ContactFormStep from 'src/components/advisor/steps/contact-form';
import ResultsList from './results-list';
import isEmpty from 'lodash/isEmpty';
import size from 'lodash/size';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = StepPageConfig & {
  className?: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-bottom: ${units(6)};
  position: relative;
  width: 100%;

  ${media.min('md')`
    padding: 0 calc(100% / 8 * 0.4) ${units(18)} calc(100% / 8);
  `}
`;

/**
 * `StyledLoading` styled component.
 */

const StyledLoading = styled(Loading)`
  top: ${units(12.5)};
  transform: translateX(-50%);
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H4)`
  color: ${color('black')};
  margin-bottom: ${units(1)};
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.Paragraph)`
  color: ${color('grey500')};
  margin-bottom: ${units(6)};
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  margin: ${units(3)} 0 ${units(6)};
`;

/**
 * `Question` styled component.
 */

const Question = styled(Type.H4)`
  margin-bottom: ${units(3)};
`;

/**
 * `ContactButton` styled component.
 */

const ContactButton = styled(Button)`
  ${media.max('md')`
    margin-bottom: ${units(5)};
  `}
`;

/**
 * `ResultsStep` component.
 */

const ResultsStep = (props: Props): ReactElement => {
  const { className, pageIndex, pageKey } = props;
  const {
    filteredModelRanges,
    isLoadingModelRanges,
    isSuccessModelRanges,
    onGoToPrevious
  } = useAdvisor();

  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const { visibleStep } = useAdvisor();
  const locomotiveScroll = useLocomotiveScroll();

  useEffect(() => {
    if (locomotiveScroll?.scroll) {
      locomotiveScroll.scroll.scrollTo('top', { duration: 200 });
    }
  }, [locomotiveScroll, visibleStep]);

  if (isSuccessModelRanges && isEmpty(filteredModelRanges)) {
    return <ContactFormStep />;
  }

  return (
    <Wrapper className={className}>
      <StyledLoading active={isLoadingModelRanges} />

      {isSuccessModelRanges && (
        <>
          <Title>
            {translate(`advisor:steps.${pageKey}.title`, {
              total: size(filteredModelRanges)
            })}
          </Title>

          <Description>
            {translate(`advisor:steps.${pageKey}.description`)}
          </Description>

          <ResultsList />

          <StyledButton
            colorTheme={'secondary'}
            onClick={() => onGoToPrevious(pageIndex)}
            size={'medium'}
          >
            {translate('common:actions.goBack')}
          </StyledButton>

          <Question>{translate('advisor:labels.cannotFind')}</Question>

          <ContactButton
            href={routeResolver('contactUs')}
            size={'medium'}
          >
            {translate('common:actions.getInTouch')}
          </ContactButton>
        </>
      )}
    </Wrapper>
  );
};

/**
 * Export `ResultsStep` component.
 */

export default ResultsStep;
